// src/pages/Respaldos.jsx — respaldos de configuración de OLT y MikroTik
// Descarga el archivo a tu computadora y guarda copia en el servidor.
import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";
import "./Respaldos.css";
import {
  Boton, EncabezadoPagina, Iconos, Tarjeta,
} from "../componentes/base";

export default function Respaldos() {
  const [olts, setOlts] = useState([]);
  const [routers, setRouters] = useState([]);
  const [copias, setCopias] = useState([]);
  const [descargando, setDescargando] = useState(null); // "olt-1" | "mk-2"
  const [mensaje, setMensaje] = useState(null); // {tipo, texto}

  // Visor de archivos (estilo editor)
  const [visor, setVisor] = useState(null);      // {nombre, contenido}
  const [cargandoVisor, setCargandoVisor] = useState(null); // nombre cargándose
  const [filtro, setFiltro] = useState("");
  const [filtroCopias, setFiltroCopias] = useState(""); // filtro de la lista

  async function cargarTodo() {
    try {
      const [rOlts, rMk, rCop] = await Promise.all([
        authFetch("/api/olt-config/"),
        authFetch("/api/mikrotik/list"),
        authFetch("/api/respaldos/"),
      ]);
      const dOlts = await rOlts.json();
      const dMk = await rMk.json();
      const dCop = await rCop.json();

      if (dOlts.status === "OK" && Array.isArray(dOlts.olts)) setOlts(dOlts.olts);
      if (Array.isArray(dMk)) setRouters(dMk);
      if (dCop.ok && Array.isArray(dCop.respaldos)) setCopias(dCop.respaldos);
    } catch (e) {
      console.log("Error cargando página de respaldos:", e);
    }
  }

  useEffect(() => {
    cargarTodo();
  }, []);

  async function descargar(tipo, equipo) {
    const clave = `${tipo}-${equipo.id}`;
    setDescargando(clave);
    setMensaje(null);
    try {
      const url =
        tipo === "olt"
          ? `/api/respaldos/olt/${equipo.id}`
          : `/api/respaldos/mikrotik/${equipo.id}`;

      const res = await authFetch(url);

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setMensaje({
          tipo: "error",
          texto: data.error || `No se pudo respaldar (${res.status})`,
        });
        return;
      }

      // Nombre de archivo desde el header del backend
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `respaldo-${clave}.txt`;

      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);

      setMensaje({ tipo: "ok", texto: `Respaldo descargado: ${filename}` });
      cargarTodo(); // refresca la lista de copias del servidor
    } catch (e) {
      setMensaje({ tipo: "error", texto: "Error de red durante el respaldo" });
    } finally {
      setDescargando(null);
    }
  }

  async function abrirVisor(nombre) {
    setCargandoVisor(nombre);
    setMensaje(null);
    try {
      const res = await authFetch(`/api/respaldos/ver/${encodeURIComponent(nombre)}`);
      const data = await res.json();
      if (data.ok) {
        setFiltro("");
        setVisor({ nombre: data.nombre, contenido: data.contenido });
      } else {
        setMensaje({ tipo: "error", texto: data.error || "No se pudo abrir el archivo" });
      }
    } catch {
      setMensaje({ tipo: "error", texto: "Error de red abriendo el archivo" });
    } finally {
      setCargandoVisor(null);
    }
  }

  async function descargarCopia(nombre) {
    try {
      const res = await authFetch(
        `/api/respaldos/ver/${encodeURIComponent(nombre)}?descargar=1`
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = nombre;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      setMensaje({ tipo: "error", texto: "Error descargando la copia" });
    }
  }

  // Líneas filtradas para el buscador del visor (tipo grep, con nº de línea)
  const lineasFiltradas = React.useMemo(() => {
    if (!visor || !filtro.trim()) return null;
    const term = filtro.toLowerCase();
    const out = [];
    const lineas = visor.contenido.split("\n");
    for (let i = 0; i < lineas.length; i++) {
      if (lineas[i].toLowerCase().includes(term)) {
        out.push({ n: i + 1, texto: lineas[i] });
        if (out.length >= 800) break; // tope de resultados por rendimiento
      }
    }
    return out;
  }, [visor, filtro]);

  const copiasFiltradas = React.useMemo(() => {
    if (!filtroCopias.trim()) return copias;
    const t = filtroCopias.toLowerCase();
    return copias.filter((c) => c.nombre.toLowerCase().includes(t));
  }, [copias, filtroCopias]);

  function tamano(bytes) {
    if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    if (bytes > 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
  }

  return (
    <div className="page-container">
      <EncabezadoPagina
        icono={Iconos.respaldos}
        titulo="Respaldos de equipos"
        resumen={`${olts.length} OLT · ${routers.length} MikroTik · ${copias.length} copias guardadas`}
      />

      {mensaje && (
        <div className={`resp-mensaje ${mensaje.tipo}`}>{mensaje.texto}</div>
      )}

      <Tarjeta titulo="OLT's">
        {olts.length === 0 ? (
          <p className="resp-vacio">Sin OLTs registradas.</p>
        ) : (
          <table className="resp-table">
            <thead>
              <tr><th>#</th><th>Nombre</th><th>Host</th><th></th></tr>
            </thead>
            <tbody>
              {olts.map((o) => (
                <tr key={o.id}>
                  <td>{o.id}</td>
                  <td>{o.nombre || "—"}</td>
                  <td>{o.host}</td>
                  <td>
                    <Boton
                      icono={Iconos.descargar}
                      cargando={descargando === `olt-${o.id}`}
                      disabled={descargando !== null}
                      onClick={() => descargar("olt", o)}
                    >
                      {descargando === `olt-${o.id}`
                        ? "Respaldando… (1-3 min)"
                        : "Respaldar configuración"}
                    </Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Tarjeta>

      <Tarjeta titulo="MikroTik">
        {routers.length === 0 ? (
          <p className="resp-vacio">Sin MikroTiks registrados.</p>
        ) : (
          <table className="resp-table">
            <thead>
              <tr><th>#</th><th>Nombre</th><th>IP</th><th></th></tr>
            </thead>
            <tbody>
              {routers.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{r.nombre || "—"}</td>
                  <td>{r.ip}</td>
                  <td>
                    <Boton
                      icono={Iconos.descargar}
                      cargando={descargando === `mk-${r.id}`}
                      disabled={descargando !== null}
                      onClick={() => descargar("mk", r)}
                    >
                      Respaldar configuración
                    </Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Tarjeta>

      <Tarjeta
        titulo="Copias guardadas en el servidor"
        acciones={
          copias.length > 0 && (
            <input
              className="resp-filtro"
              placeholder="Filtrar por nombre (olt, mikrotik, fecha…)"
              value={filtroCopias}
              onChange={(e) => setFiltroCopias(e.target.value)}
            />
          )
        }
      >
        {copias.length === 0 ? (
          <p className="resp-vacio">
            Aún no hay copias. Cada respaldo que descargues deja una copia aquí
            (y entra al respaldo nocturno del servidor).
          </p>
        ) : (
          <div className="resp-lista-scroll">
            <table className="resp-table">
              <thead>
                <tr><th>Archivo</th><th>Tamaño</th><th>Fecha</th><th></th></tr>
              </thead>
              <tbody>
                {copiasFiltradas.map((c) => (
                  <tr key={c.nombre}>
                    <td>{c.nombre}</td>
                    <td>{tamano(c.bytes)}</td>
                    <td>{c.fecha}</td>
                    <td className="resp-acciones">
                      <Boton
                        variante="secundario"
                        icono={Iconos.documento}
                        cargando={cargandoVisor === c.nombre}
                        disabled={cargandoVisor !== null}
                        onClick={() => abrirVisor(c.nombre)}
                      >
                        Ver
                      </Boton>
                      <Boton
                        variante="secundario"
                        icono={Iconos.descargar}
                        onClick={() => descargarCopia(c.nombre)}
                      >
                        Descargar
                      </Boton>
                    </td>
                  </tr>
                ))}
                {copiasFiltradas.length === 0 && (
                  <tr><td colSpan={4} className="resp-vacio">Sin coincidencias</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>

      {/* ================= VISOR DE ARCHIVOS (estilo editor) ================= */}
      {visor && (
        <div className="visor-overlay" onClick={() => setVisor(null)}>
          <div className="visor-editor" onClick={(e) => e.stopPropagation()}>
            <div className="visor-barra">
              <span className="visor-nombre">
                <Iconos.documento size={15} strokeWidth={1.8} />
                {visor.nombre}
              </span>
              <input
                className="visor-buscar"
                placeholder="Buscar… (cliente, SN, vlan, ip)"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                autoFocus
              />
              <button className="visor-cerrar" onClick={() => setVisor(null)} aria-label="Cerrar">
                <Iconos.cerrar size={17} strokeWidth={2} />
              </button>
            </div>

            {lineasFiltradas ? (
              <div className="visor-contenido">
                <div className="visor-resultados">
                  {lineasFiltradas.length === 0
                    ? "Sin coincidencias"
                    : `${lineasFiltradas.length} línea(s) encontrada(s)${
                        lineasFiltradas.length >= 800 ? " (mostrando primeras 800)" : ""
                      }`}
                </div>
                <pre>
                  {lineasFiltradas.map((l) => (
                    <div key={l.n} className="visor-linea">
                      <span className="visor-num">{l.n}</span>
                      {l.texto}
                    </div>
                  ))}
                </pre>
              </div>
            ) : (
              <div className="visor-contenido">
                <pre>{visor.contenido}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
